/* ============================================================
   BLUER — Shopify Theme JS
   Cart AJAX, Nav Drawer, Variant Selection, Filters
   ============================================================ */

'use strict';

/* ---- Helpers --------------------------------------------- */
function $(sel, ctx) { return (ctx || document).querySelector(sel); }
function $$(sel, ctx) { return [...(ctx || document).querySelectorAll(sel)]; }

// Preserve scroll position when navigating back to collection pages
if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

(function initScrollRestoration() {
  const suffix = () => window.location.pathname + window.location.search;
  const key = () => 'scroll_pos_' + suffix();

  // The document height is saved alongside the offset. On the way back the page
  // starts far shorter (only the first paginated batch, images not yet decoded),
  // so without reserving that height the browser clamps the scroll to the current
  // bottom and the position has to creep down as content loads. Reserving it lets
  // the scroll land in ONE jump, before the first paint.
  const save = () => {
    try {
      sessionStorage.setItem(key(), String(window.scrollY));
      sessionStorage.setItem('scroll_h_' + suffix(), String(document.documentElement.scrollHeight));
    } catch (_) { }
  };

  const reveal = () => document.documentElement.classList.remove('is-restoring-scroll');

  // `beforeunload` alone was the reason this never worked on phones: iOS Safari
  // and Android Chrome usually freeze a page into the bfcache instead of
  // unloading it, and `beforeunload` simply never fires there. Nothing was ever
  // saved, so there was nothing to come back to. `pagehide` and
  // `visibilitychange` do fire in that situation.
  window.addEventListener('pagehide', save);
  window.addEventListener('beforeunload', save);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') save();
  });

  let restoring = false;

  const restoreScroll = () => {
    let target = NaN;
    try { target = parseInt(sessionStorage.getItem(key()), 10); } catch (_) { return; }
    if (!target || isNaN(target) || target <= 0 || restoring) return;

    restoring = true;
    const startedAt = Date.now();

    // Reserve the height the page had when we left, so the very first scrollTo()
    // below can reach the saved offset immediately instead of being clamped to a
    // short document and then creeping down as products load.
    const savedHeight = (window.__bluerScrollRestore || {}).height || 0;
    if (savedHeight > document.documentElement.scrollHeight && document.body) {
      document.body.style.minHeight = savedHeight + 'px';
    }

    window.scrollTo(0, target);
    reveal(); // Position is correct — safe to show the page now.

    // The hold loop below re-applies the offset while the page is still growing.
    // If the user starts scrolling during that window it would drag them straight
    // back — which feels exactly like the page refusing to scroll. Any real input
    // wins immediately and ends the restore.
    const HOLD_EVENTS = ['wheel', 'touchstart', 'touchmove', 'keydown', 'pointerdown'];
    let cancelled = false;
    const endHold = () => {
      if (cancelled) return;
      cancelled = true;
      if (document.body) document.body.style.minHeight = '';
      restoring = false;
      HOLD_EVENTS.forEach(evt => window.removeEventListener(evt, endHold));
    };
    HOLD_EVENTS.forEach(evt => window.addEventListener(evt, endHold, { passive: true }));

    // The infinite-scroll loader keeps appending products and the lazy images
    // keep decoding, both of which shift the height. Hold the offset while that
    // settles, then release the reserved height so the footer sits correctly.
    // 800ms, not 3s: the offset lands in one jump now, and a long window just
    // meant more time in which the loop could fight the user on a slow phone.
    (function step() {
      if (cancelled) return;

      const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      if (Math.abs(window.scrollY - target) > 2) window.scrollTo(0, Math.min(target, max));

      if (Date.now() - startedAt < 800) {
        requestAnimationFrame(step);
      } else {
        endHold();
      }
    })();
  };

  // Only restore when the user actually came BACK. Opening the collection fresh
  // from the menu should start at the top, not drop them where they were before.
  const isBackNavigation = () => {
    try {
      const nav = performance.getEntriesByType('navigation')[0];
      if (nav && nav.type) return nav.type === 'back_forward';
      return performance.navigation && performance.navigation.type === 2;
    } catch (_) { return false; }
  };

  // Run as early as possible. theme.js sits at the bottom of <body>, so the DOM
  // is already there and the head script has hidden the page — restoring now
  // means the first frame the user sees is already at the right offset, instead
  // of the old behaviour of painting the top and then scrolling down to it.
  if (window.__bluerScrollRestore) {
    restoreScroll();
  } else {
    reveal();
  }

  // bfcache restores never re-fire `load`, and because scrollRestoration is set
  // to 'manual' above the browser will not put the position back either — so on
  // mobile this listener is what makes "back" work at all.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted || isBackNavigation()) restoreScroll();
    else reveal();
  });
})();

/* ---- Release any scroll lock left behind by a bfcache restore ----
   The size guide, notify-me modal, cart drawer and nav drawer all lock the page
   with `document.body.style.overflow = 'hidden'` (plus a paddingRight nudge).
   Mobile browsers freeze a page into the bfcache instead of unloading it, so if
   the visitor opened one of those on a product page and then navigated, coming
   back restored the DOM with the lock still applied — the list simply would not
   scroll. Nothing can legitimately be open at the moment a page is shown, so
   clear the lock unconditionally here. */
(function releaseStaleScrollLock() {
  const unlock = () => {
    if (!document.body) return;
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    document.documentElement.classList.remove('is-menu-open', 'is-search-modal-open');
    const header = document.getElementById('site-header');
    if (header) header.style.paddingRight = '';
  };

  window.addEventListener('pageshow', (e) => { if (e.persisted) unlock(); });
  window.addEventListener('popstate', unlock);
})();

function formatMoney(cents) {
  // Match the Liquid `| money` output for India: "Rs. 1,790" (no decimals, comma thousands)
  const amount = Math.round(cents / 100);
  const withCommas = amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return 'Rs. ' + withCommas;
}

/* ---- Cart ------------------------------------------------- */
const Cart = {
  async get() {
    const res = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
    return res.json();
  },

  async add(variantId, quantity = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity })
    });
    return res.json();
  },

  async update(updates) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    return res.json();
  },

  async change(variantId, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity })
    });
    return res.json();
  }
};

/* ---- Cart Count ------------------------------------------ */
// function updateCartCount(count) {
//   // Header bag: render as bracketed text — "Your Bag (2)" — hidden when empty
//   const el = $('#cart-count');
//   if (el) {
//     el.innerHTML = count > 0 ? '&nbsp;[' + count + ']' : '';
//   }
//   // Mobile bottom-nav and product page top-bar bag badges
//   $$('.mobile-nav-bag-count').forEach(badge => {
//     badge.textContent = count;
//     badge.style.display = count > 0 ? '' : 'none';
//   });
//   // Side drawer cart tab header count
//   const drawerCount = $('#cart-drawer-count');
//   if (drawerCount) {
//     drawerCount.textContent = count;
//   }
// }

function updateCartCount(count) {
  // Header bag count
  const el = $('#cart-count');
  if (el) {
    el.innerHTML = count > 0 ? '&nbsp;[' + count + ']' : '';
  }

  // Mobile bottom-nav and product page bag badges
  $$('.mobile-nav-bag-count').forEach(badge => {
    badge.textContent = count;
    badge.style.setProperty(
      'display',
      count > 0 ? 'flex' : 'none',
      'important'
    );
  });

  // Side drawer cart count
  const drawerCount = $('#cart-drawer-count');
  if (drawerCount) {
    drawerCount.textContent = count;
  }
}

/* ---- ATB Toast ------------------------------------------- */
let toastTimer;
function showToast(message) {
  let toast = $('#atb-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'atb-toast';
    toast.className = 'atb-toast';
    document.body.appendChild(toast);
  }
  toast.textContent = message;
  toast.classList.add('is-visible');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 2500);
}

/* ---- Cart Drawer ----------------------------------------- */
const CartDrawer = {
  el: null,
  bodyEl: null,
  footerEl: null,

  init() {
    this.el = $('#cart-drawer');
    this.bodyEl = $('#cart-drawer-body');
    this.footerEl = $('#cart-drawer-footer');

    // On standalone /cart page, ensure side drawer is closed
    if (window.location.pathname === '/cart') {
      this.close();
    }

    const closeBtn = $('#cart-close');
    const overlay = $('#cart-overlay');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      });
    }
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      });
    }

    // Tab switching between YOUR BAG and LOOKBOOK inside side drawer
    if (this.el) {
      $$('.cart-tab-btn', this.el).forEach(btn => {
        btn.addEventListener('click', () => {
          const tab = btn.dataset.tab;
          $$('.cart-tab-btn', this.el).forEach(b => b.classList.toggle('is-active', b === btn));
          const bagPanel = $('[data-drawer-panel="bag"]', this.el);
          const looksPanel = $('[data-drawer-panel="looks"]', this.el);
          if (bagPanel && looksPanel) {
            if (tab === 'looks') {
              bagPanel.style.display = 'none';
              looksPanel.style.display = 'flex';
            } else {
              bagPanel.style.display = 'flex';
              looksPanel.style.display = 'none';
            }
          }
        });
      });

      // Swipe right to close side drawer on mobile
      let touchStartX = 0;
      let touchStartY = 0;
      const panel = $('.cart-drawer__panel', this.el) || this.el;

      panel.addEventListener('touchstart', (e) => {
        if (e.touches && e.touches.length === 1) {
          touchStartX = e.touches[0].clientX;
          touchStartY = e.touches[0].clientY;
        }
      }, { passive: true });

      panel.addEventListener('touchend', (e) => {
        if (e.changedTouches && e.changedTouches.length === 1) {
          const touchEndX = e.changedTouches[0].clientX;
          const touchEndY = e.changedTouches[0].clientY;
          const deltaX = touchEndX - touchStartX;
          const deltaY = touchEndY - touchStartY;

          // Swiping right to close drawer
          if (deltaX > 50 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
            this.close();
          }
        }
      }, { passive: true });
    }

    // Automatically close drawer if window is resized to desktop width (>= 768px)
    window.addEventListener('resize', () => {
      if (window.innerWidth >= 768) {
        this.close();
      }
    });

    // Delegate clicks on cart triggers: Mobile only (< 768px)
    document.addEventListener('click', (e) => {
      const cartTrigger = e.target.closest('.header-nav__bag, a[href="/cart"], [data-open-cart], .mobile-nav-bag-wrap');
      if (cartTrigger && window.location.pathname !== '/cart') {
        if (window.innerWidth < 768) {
          e.preventDefault();
          this.open();
        }
      }
    });
  },

  setTab(tabName) {
    if (!this.el) return;
    const targetBtn = $(`.cart-tab-btn[data-tab="${tabName}"]`, this.el);
    if (targetBtn) {
      $$('.cart-tab-btn', this.el).forEach(b => b.classList.toggle('is-active', b === targetBtn));
      const bagPanel = $('[data-drawer-panel="bag"]', this.el);
      const looksPanel = $('[data-drawer-panel="looks"]', this.el);
      if (bagPanel && looksPanel) {
        if (tabName === 'looks') {
          bagPanel.style.display = 'none';
          looksPanel.style.display = 'flex';
        } else {
          bagPanel.style.display = 'flex';
          looksPanel.style.display = 'none';
        }
      }
    }
  },

  open(tab) {
    if (!this.el || window.location.pathname === '/cart') return;
    if (typeof SearchModal !== 'undefined' && SearchModal.container && SearchModal.container.classList.contains('is-open')) {
      SearchModal.close();
    }
    this.setTab(tab || 'bag');
    this.el.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('is-cart-drawer-open');
    document.body.classList.add('is-cart-drawer-open');
    document.body.style.overflow = 'hidden';
    this.refresh();

    // Highlight Bag button in mobile bottom nav
    const bagBtn = $('a[href="/cart"].mobile-nav-item');
    if (bagBtn) bagBtn.classList.add('is-active');
  },

  close() {
    if (!this.el) return;
    this.el.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('is-cart-drawer-open');
    document.body.classList.remove('is-cart-drawer-open');
    document.body.style.overflow = '';
    setTimeout(() => this.setTab('bag'), 300);

    // Remove highlight on Bag button in mobile bottom nav (only if not on the actual cart page template)
    const bagBtn = $('a[href="/cart"].mobile-nav-item');
    if (bagBtn && window.location.pathname !== '/cart') {
      bagBtn.classList.remove('is-active');
    }
  },

  showSkeleton() {
    if (!this.bodyEl) return;
    this.bodyEl.innerHTML = `
      <div class="cart-skeleton-list" style="display: flex; flex-direction: column; gap: 16px; padding: 4px 0;">
        ${Array(5).fill(0).map(() => `
          <div class="cart-skeleton-item" style="display: flex; gap: 16px; align-items: flex-start;">
            <div style="width: 70px; height: 95px; background: #e0e0e0; flex-shrink: 0; animation: skeleton-pulse 1.2s infinite ease-in-out;"></div>
            <div style="flex: 1; display: flex; flex-direction: column; gap: 8px;">
              <div style="width: 75%; height: 12px; background: #e0e0e0; animation: skeleton-pulse 1.2s infinite ease-in-out;"></div>
              <div style="width: 40%; height: 10px; background: #ececec; animation: skeleton-pulse 1.2s infinite ease-in-out;"></div>
              <div style="width: 50%; height: 12px; background: #e0e0e0; animation: skeleton-pulse 1.2s infinite ease-in-out; margin-top: 4px;"></div>
              <div style="display: flex; gap: 12px; margin-top: 8px;">
                <div style="width: 64px; height: 22px; background: #e0e0e0; animation: skeleton-pulse 1.2s infinite ease-in-out;"></div>
                <div style="width: 40px; height: 22px; background: #e0e0e0; animation: skeleton-pulse 1.2s infinite ease-in-out;"></div>
              </div>
            </div>
          </div>
        `).join('')}
      </div>
    `;
  },

  async refresh() {
    this.showSkeleton();
    const cart = await Cart.get();
    updateCartCount(cart.item_count);
    this.render(cart);
  },

  productCache: {},

  async fetchProductVariants(handle) {
    if (!handle) return null;
    if (this.productCache[handle]) return this.productCache[handle];
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (res.ok) {
        const data = await res.json();
        this.productCache[handle] = data;
        return data;
      }
    } catch (e) { }
    return null;
  },

  async render(cart) {
    if (!this.bodyEl) return;
    if (cart.item_count === 0) {
      this.bodyEl.innerHTML = `
        <div class="cart-empty-page">
          <p class="cart-empty-title">YOUR BAG IS EMPTY</p>
          <p class="cart-empty-sub">Explore the latest arrivals and more</p>
          <a href="/collections/all" class="btn cart-empty-cta" style="display: flex; align-items: center; justify-content: center; height: 35px; min-height: 35px; line-height: 35px; padding: 0 20px; font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase;">VIEW COLLECTION</a>
        </div>
      `;
      if (this.footerEl) this.footerEl.style.display = 'none';
      return;
    }

    // Fetch product info in parallel for all items to populate all size options
    await Promise.all(cart.items.map(item => item.handle ? this.fetchProductVariants(item.handle) : Promise.resolve(null)));

    const items = cart.items.map(item => {
      const productData = this.productCache[item.handle];
      let optionsHtml = `<option value="${item.variant_id}" selected>${item.variant_title}</option>`;
      let compareAtPrice = 0;
      if (productData && productData.variants && productData.variants.length > 0) {
        optionsHtml = productData.variants.map(v => {
          let label = v.title;
          if (v.option2 && v.option2 !== 'Default Title') label = v.option2;
          else if (v.option1 && v.option1 !== 'Default Title') label = v.option1;
          const isSelected = String(v.id) === String(item.variant_id);
          if (isSelected && v.compare_at_price) {
            compareAtPrice = v.compare_at_price;
          }
          return `<option value="${v.id}" ${isSelected ? 'selected' : ''} ${!v.available ? 'disabled' : ''}>${label}</option>`;
        }).join('');
      }

      const showCompare = (compareAtPrice && compareAtPrice > item.price) || (item.original_price && item.original_price > item.price) || (item.original_line_price && item.original_line_price > item.final_line_price);
      const compareAmount = compareAtPrice > item.price ? compareAtPrice * item.quantity : (item.original_price > item.price ? item.original_price * item.quantity : item.original_line_price);

      return `
        <div class="cart-drawer__item">
          <div class="cart-drawer__item-img">
            <a href="${item.url}" style="display:block;width:100%;height:100%;">
              ${item.image ? `<img src="${item.image}" alt="${item.product_title}" loading="lazy" style="display:block;width:100%;height:100%;object-fit:cover;">` : ''}
            </a>
          </div>
          <div style="display:flex;flex-direction:column;justify-content:flex-start;gap:6px;min-width:0;">
            <div>
              <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:8px;">
                <p class="cart-drawer__item-title" style="margin:0;flex:1;line-height:1.3;"><a href="${item.url}" style="color:inherit;text-decoration:none;">${item.product_title}</a></p>
                <button class="btn-reset" data-remove="${item.variant_id}" aria-label="Remove ${item.product_title}" style="display:inline-flex;align-items:flex-start;justify-content:flex-start;color:#000000;cursor:pointer;opacity:0.7;background:none;border:none;flex-shrink:0;">
                  <svg width="9" height="9" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.3"><path d="M1 1l12 12M13 1L1 13" stroke-linecap="square"/></svg>
                </button>
              </div>
              <p class="cart-drawer__item-price" style="margin-top:2px;">
                ${showCompare ? `<s style="margin-right:6px;opacity:0.5;font-weight:400;">${formatMoney(compareAmount)}</s>` : ''}
                <span>${formatMoney(item.final_line_price)}</span>
              </p>
            </div>
            <div style="display:flex;align-items:center;gap:10px;margin-top:20px;">
              <div class="quantity-control">
                <button class="qty-btn btn-reset" data-variant="${item.variant_id}" data-action="decrease">−</button>
                <span class="qty-display">${item.quantity}</span>
                <button class="qty-btn btn-reset" data-variant="${item.variant_id}" data-action="increase">+</button>
              </div>
              ${item.variant_title && item.variant_title !== 'Default Title' ? `
                <div class="cart-size-select" style="position:relative;display:inline-flex;align-items:center;">
                  <select class="cart-item__size cart-item__size-select" data-line-variant="${item.variant_id}" data-qty="${item.quantity}" aria-label="Change size" style="-webkit-appearance:none;appearance:none;background:#f6f6f6;border:none;outline:none;box-shadow:none;height:24px;padding:0 18px 0 8px;font-size:10px;letter-spacing:0.06em;color:#000000;font-weight:300;border-radius:0;cursor:pointer;">
                    ${optionsHtml}
                  </select>
                  <svg class="cart-size-select__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="position:absolute;right:4px;top:50%;transform:translateY(-50%);width:10px;height:10px;pointer-events:none;color:#000000;"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/></svg>
                </div>
              ` : ''}
            </div>
          </div>
        </div>
      `;
    }).join('');

    this.bodyEl.innerHTML = items;
    if (this.footerEl) {
      this.footerEl.style.display = 'flex';
      const subtotalEl = $('#cart-subtotal-amount');
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    }

    // Quantity controls inside drawer — Silent updates without image blinking
    $$('[data-action="decrease"]', this.bodyEl).forEach(btn => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset.variant;
        const itemRow = btn.closest('.cart-drawer__item');
        const qtyDisplay = $('.qty-display', itemRow);
        const priceDisplay = $('.cart-drawer__item-price', itemRow);

        const currentQty = parseInt(qtyDisplay ? qtyDisplay.textContent : '1', 10);
        const newQty = Math.max(0, currentQty - 1);

        if (newQty === 0) {
          const updated = await Cart.change(id, 0);
          updateCartCount(updated.item_count);
          if (updated.item_count === 0) {
            this.render(updated);
          } else {
            itemRow && itemRow.remove();
            const subtotalEl = $('#cart-subtotal-amount');
            if (subtotalEl) subtotalEl.textContent = formatMoney(updated.total_price);
          }
          return;
        }

        if (qtyDisplay) qtyDisplay.textContent = newQty;
        const updated = await Cart.change(id, newQty);
        updateCartCount(updated.item_count);
        const updatedItem = updated.items.find(i => String(i.variant_id) === String(id));
        if (updatedItem && priceDisplay) {
          priceDisplay.textContent = formatMoney(updatedItem.final_line_price);
        }
        const subtotalEl = $('#cart-subtotal-amount');
        if (subtotalEl) subtotalEl.textContent = formatMoney(updated.total_price);
      });
    });

    $$('[data-action="increase"]', this.bodyEl).forEach(btn => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset.variant;
        const itemRow = btn.closest('.cart-drawer__item');
        const qtyDisplay = $('.qty-display', itemRow);
        const priceDisplay = $('.cart-drawer__item-price', itemRow);

        const currentQty = parseInt(qtyDisplay ? qtyDisplay.textContent : '1', 10);
        const newQty = currentQty + 1;

        if (qtyDisplay) qtyDisplay.textContent = newQty;
        const updated = await Cart.change(id, newQty);
        updateCartCount(updated.item_count);
        const updatedItem = updated.items.find(i => String(i.variant_id) === String(id));
        if (updatedItem && priceDisplay) {
          priceDisplay.textContent = formatMoney(updatedItem.final_line_price);
        }
        const subtotalEl = $('#cart-subtotal-amount');
        if (subtotalEl) subtotalEl.textContent = formatMoney(updated.total_price);
      });
    });

    $$('[data-remove]', this.bodyEl).forEach(btn => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset.remove;
        const itemRow = btn.closest('.cart-drawer__item');
        const updated = await Cart.change(id, 0);
        updateCartCount(updated.item_count);
        if (updated.item_count === 0) {
          this.render(updated);
        } else {
          itemRow && itemRow.remove();
          const subtotalEl = $('#cart-subtotal-amount');
          if (subtotalEl) subtotalEl.textContent = formatMoney(updated.total_price);
        }
      });
    });

    $$('.cart-item__size-select', this.bodyEl).forEach(sel => {
      sel.addEventListener('change', async () => {
        const oldVariant = sel.dataset.lineVariant;
        const newVariant = sel.value;
        const qty = parseInt(sel.dataset.qty || '1', 10);
        if (!newVariant || newVariant === oldVariant) return;

        await Cart.change(oldVariant, 0);
        await Cart.add(newVariant, qty);
        const fullCart = await Cart.get();
        updateCartCount(fullCart.item_count);

        sel.dataset.lineVariant = newVariant;
        const itemRow = sel.closest('.cart-drawer__item');
        if (itemRow) {
          const decBtn = $('[data-action="decrease"]', itemRow);
          const incBtn = $('[data-action="increase"]', itemRow);
          const remBtn = $('[data-remove]', itemRow);
          if (decBtn) decBtn.dataset.variant = newVariant;
          if (incBtn) incBtn.dataset.variant = newVariant;
          if (remBtn) remBtn.dataset.remove = newVariant;
        }

        const subtotalEl = $('#cart-subtotal-amount');
        if (subtotalEl) subtotalEl.textContent = formatMoney(fullCart.total_price);
      });
    });
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => CartDrawer.init());
} else {
  CartDrawer.init();
}

/* ---- Nav Drawer ------------------------------------------ */
const NavDrawer = {
  drawer: null,
  overlay: null,
  activePanel: null,

  init() {
    this.drawer = $('#nav-drawer');
    this.overlay = $('#nav-overlay');

    const openBtn = $('#menu-toggle');
    const closeBtn = $('#menu-close');
    if (openBtn) openBtn.addEventListener('click', () => {
      this.drawer && this.drawer.classList.contains('is-open') ? this.close() : this.open();
    });
    if (closeBtn) closeBtn.addEventListener('click', () => this.close());
    if (this.overlay) this.overlay.addEventListener('click', () => this.close());

    // Category toggle buttons & links
    $$('.nav-sidebar-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const href = btn.getAttribute('href') || '';
        if (href.includes('looks')) return; // Handled by LOOKBOOK interceptor below
        const target = btn.dataset.panel;
        if (target) {
          this.setPanel(target, btn);
        } else {
          this.close();
        }
      });
      btn.addEventListener('mouseenter', () => {
        const target = btn.dataset.panel;
        if (target) {
          this.setPanel(target, btn);
          return;
        }
        // Items with no sub-links (rendered as <a class="nav-sidebar-link">) have
        // no data-panel, so setPanel() never ran for them — and setPanel() is the
        // only thing that clears `.is-active`. The previously hovered category
        // therefore kept its highlight while this item lit up via :hover, showing
        // TWO highlighted items at once.
        //
        // Clear BOTH the stale highlight and the stale sub-links panel: this item
        // has no children, so leaving the previous category's links on screen made
        // them look like they belonged to it. `this.activePanel` still remembers
        // the real panel, and the sidebar's mouseleave below puts it back.
        $$('.nav-sidebar-btn').forEach(b => b.classList.remove('is-active'));
        $$('.nav-links-panel').forEach(p => p.classList.remove('is-active'));
      });
    });

    // Leaving the sidebar restores the last real category — both its highlight and
    // its sub-links — after the pointer has passed over an item with no children.
    const navSidebar = $('.nav-sidebar');
    if (navSidebar) {
      navSidebar.addEventListener('mouseleave', () => {
        if (!this.activePanel) return;
        const owner = $(`.nav-sidebar-btn[data-panel="${this.activePanel}"]`);
        this.setPanel(this.activePanel, owner);
      });
    }

    // Intercept LOOKBOOK links to open the Cart Drawer LOOKBOOK tab directly
    $$('a[href*="view=looks"], a[href*="looks"], .nav-sidebar-btn[href*="looks"]', this.drawer).forEach(link => {
      link.addEventListener('click', (e) => {
        if (window.location.pathname !== '/cart') {
          e.preventDefault();
          e.stopPropagation();
          this.close();
          setTimeout(() => {
            CartDrawer.open('looks');
          }, 50);
        }
      });
    });

    // Close menu when clicking sub-links or extra links
    $$('.nav-link, .nav-extra-link, .nav-collection-tile').forEach(link => {
      link.addEventListener('click', () => {
        this.close();
      });
    });

    // Open the "Clothing" panel by default (design), falling back to the first.
    const defaultBtn = $('.nav-sidebar-btn[data-default="true"]') || $('.nav-sidebar-btn');
    if (defaultBtn) {
      this.setPanel(defaultBtn.dataset.panel, defaultBtn);
    }
  },

  setPanel(panelId, btn) {
    $$('.nav-sidebar-btn').forEach(b => b.classList.remove('is-active'));
    $$('.nav-links-panel').forEach(p => p.classList.remove('is-active'));
    if (btn) btn.classList.add('is-active');
    const panel = $(`#${panelId}`);
    if (panel) panel.classList.add('is-active');
    // Remembered so the sidebar's mouseleave can restore this panel after the
    // pointer has passed over an item that has no sub-links of its own.
    if (panel) this.activePanel = panelId;
  },

  open() {
    if (!this.drawer) return;
    this.drawer.classList.add('is-open');
    if (this.overlay) this.overlay.classList.add('is-open');

    // Close the search modal if it is open. The mobile search button already
    // closes the nav drawer, but the reverse was missing — so opening the menu
    // from an open search left `is-search-modal-open` on <html> alongside
    // `is-menu-open`. The bottom bar then styled BOTH icons black (the search
    // icon is forced black by html.is-search-modal-open regardless of
    // .is-active), so search and menu both looked selected. It also left the
    // search panel sitting open behind the drawer.
    var searchContainer = document.getElementById('search-modal-container');
    if (searchContainer && searchContainer.classList.contains('is-open')) {
      SearchModal.close();
    }

    // Close notification modal if open
    var notifModal = document.getElementById('home-notification-modal');
    if (notifModal && notifModal.classList.contains('is-active')) {
      notifModal.classList.remove('is-active');
      notifModal.setAttribute('aria-hidden', 'true');
    }

    document.documentElement.classList.add('is-menu-open');
    document.body.style.overflow = 'hidden';

    // Clear all active states and highlight only the Menu button
    $$('.mobile-nav-item').forEach(el => el.classList.remove('is-active'));
    const mobileMenuBtn = $('#mobile-menu-btn');
    if (mobileMenuBtn) mobileMenuBtn.classList.add('is-active');
  },

  close() {
    if (!this.drawer) return;
    this.drawer.classList.remove('is-open');
    if (this.overlay) this.overlay.classList.remove('is-open');
    document.documentElement.classList.remove('is-menu-open');
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    const header = document.getElementById('site-header');
    if (header) header.style.paddingRight = '';

    // Restore active state based on current page URL
    $$('.mobile-nav-item').forEach(el => el.classList.remove('is-active'));
    const path = window.location.pathname;
    if (path === '/' || path === '') {
      const homeLink = $('a[href="/"].mobile-nav-item') || $('a[href="' + window.Shopify.routes.root + '"].mobile-nav-item');
      if (homeLink) homeLink.classList.add('is-active');
    } else if (path === '/cart') {
      const bagLink = $('a[href="/cart"].mobile-nav-item');
      if (bagLink) bagLink.classList.add('is-active');
    } else if (path.indexOf('/search') === 0) {
      const searchBtn = $('#mobile-search-btn');
      if (searchBtn) searchBtn.classList.add('is-active');
    } else if (path.startsWith('/account')) {
      const accountLink = $('a[href="/account"].mobile-nav-item');
      if (accountLink) accountLink.classList.add('is-active');
    }

    // Clear hash without pushing empty states to history
    if (window.location.hash === '#menu') {
      history.replaceState(null, null, window.location.pathname + window.location.search);
    }
  }
};

/* ---- Search Modal Component ----------------------------- */
const SearchModal = {
  container: null,
  backdrop: null,
  closeBtn: null,

  init() {
    this.container = $('#search-modal-container');
    this.backdrop = $('#search-modal-backdrop');
    this.closeBtn = $('#search-modal-close');

    if (!this.container) return;

    const headerInput = $('#header-search-input');
    const pageInput = $('#search-page-input');
    const headerSearchCloseBtn = $('#header-search-close-btn');

    if (headerSearchCloseBtn) {
      headerSearchCloseBtn.style.display = 'none';
    }
    if (headerInput) {
      headerInput.value = '';
    }
    if (pageInput) {
      pageInput.value = '';
    }

    if (headerInput) {
      headerInput.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
      headerInput.addEventListener('focus', (e) => {
        if (!this.container || !this.container.classList.contains('is-open')) {
          e.preventDefault();
          this.open();
        }
      });
    }

    const headerSearchLabel = $('.header-search label[for="header-search-input"]');
    if (headerSearchLabel) {
      headerSearchLabel.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    }

    // Sync input values bidirectionally so typing/clearing either input updates both
    if (headerInput && pageInput) {
      let isSyncing = false;

      headerInput.addEventListener('input', () => {
        if (isSyncing) return;
        isSyncing = true;
        pageInput.value = headerInput.value;
        pageInput.dispatchEvent(new Event('input', { bubbles: true }));

        if (headerSearchCloseBtn) {
          headerSearchCloseBtn.style.display = headerInput.value.length > 0 ? 'block' : 'none';
        }

        if (!headerInput.value.trim()) {
          document.documentElement.classList.remove('search-is-typing');
          const liveResults = $('#search-page-live-results');
          if (liveResults) {
            liveResults.innerHTML = '';
            liveResults.style.display = 'none';
          }
        }
        isSyncing = false;
      });

      pageInput.addEventListener('input', () => {
        if (isSyncing) return;
        isSyncing = true;
        headerInput.value = pageInput.value;

        if (headerSearchCloseBtn) {
          headerSearchCloseBtn.style.display = pageInput.value.length > 0 ? 'block' : 'none';
        }

        if (!pageInput.value.trim()) {
          document.documentElement.classList.remove('search-is-typing');
          const liveResults = $('#search-page-live-results');
          if (liveResults) {
            liveResults.innerHTML = '';
            liveResults.style.display = 'none';
          }
        }
        isSyncing = false;
      });
    }

    const headerForm = $('#header-search-input')?.closest('form') || $('.header-search form');
    if (headerForm) {
      headerForm.addEventListener('submit', (e) => {
        if (location.pathname.indexOf('/search') !== 0) {
          e.preventDefault();
          this.open();
        }
      });
    }

    if (headerSearchCloseBtn) {
      headerSearchCloseBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (headerInput) headerInput.value = '';
        if (pageInput) pageInput.value = '';
        document.documentElement.classList.remove('search-is-typing');
        const liveResults = $('#search-page-live-results');
        if (liveResults) {
          liveResults.innerHTML = '';
          liveResults.style.display = 'none';
        }
        headerSearchCloseBtn.style.display = 'none';
        if (headerInput) headerInput.focus();
      });
    }

    const headerSearchExitBtn = $('#header-search-exit-btn');
    if (headerSearchExitBtn) {
      headerSearchExitBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      });
    }

    // Close actions
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
    if (this.backdrop) this.backdrop.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.container.classList.contains('is-open')) {
        this.close();
      }
    });
  },

  open() {
    if (!this.container) return;

    // Close nav drawer if open
    if (typeof NavDrawer !== 'undefined' && NavDrawer.drawer && NavDrawer.drawer.classList.contains('is-open')) {
      NavDrawer.close();
    }

    // Close notification modal if open
    var notifModal = document.getElementById('home-notification-modal');
    if (notifModal && notifModal.classList.contains('is-active')) {
      notifModal.classList.remove('is-active');
      notifModal.setAttribute('aria-hidden', 'true');
    }

    this.container.classList.add('is-open');
    document.documentElement.classList.add('is-search-modal-open');
    document.body.style.overflow = 'hidden';

    const headerInput = $('#header-search-input');
    const headerSearchCloseBtn = $('#header-search-close-btn');
    if (headerSearchCloseBtn) {
      headerSearchCloseBtn.style.display = (headerInput && headerInput.value.length > 0) ? 'block' : 'none';
    }
    const headerSearchExitBtn = $('#header-search-exit-btn');
    if (headerSearchExitBtn) {
      headerSearchExitBtn.style.display = 'block';
    }

    // Clear active state from all bottom nav items and highlight Search button
    $$('.mobile-nav-item').forEach(el => el.classList.remove('is-active'));
    const searchBtn = $('#mobile-search-btn');
    if (searchBtn) searchBtn.classList.add('is-active');

    // Auto-focus header input on desktop, or search page input on mobile
    const isDesktop = window.innerWidth >= 768;
    const pageInput = $('#search-page-input');
    const targetInput = (isDesktop && headerInput) ? headerInput : (pageInput || headerInput);

    // iOS Safari raises the soft keyboard ONLY for a focus() that happens
    // synchronously inside the originating tap. The classes above were just
    // added, so force a style/layout flush here — otherwise the input is still
    // unfocusable on this line and the focus silently no-ops, leaving just the
    // delayed fallback below, which iOS will not honour for the keyboard.
    // (This is why search opened the keyboard on Android but not on iPhone.)
    if (this.container) void this.container.offsetHeight;

    if (targetInput) {
      targetInput.focus();
    }

    // Fallback only — covers browsers where layout wasn't ready. iOS will
    // focus but not open the keyboard from here, which is expected.
    setTimeout(() => {
      if (targetInput && document.activeElement !== targetInput) {
        targetInput.focus();
      }
    }, 150);
  },

  close() {
    if (!this.container) return;
    this.container.classList.remove('is-open');
    document.documentElement.classList.remove('is-search-modal-open');
    document.documentElement.classList.remove('search-is-typing');

    // Remove highlight on Search button in mobile bottom nav (only if not on the actual search results page)
    const searchBtn = $('#mobile-search-btn');
    if (searchBtn && window.location.pathname.indexOf('/search') !== 0) {
      searchBtn.classList.remove('is-active');
    }

    const headerSearchCloseBtn = $('#header-search-close-btn');
    if (headerSearchCloseBtn) {
      headerSearchCloseBtn.style.display = 'none';
    }
    const headerSearchExitBtn = $('#header-search-exit-btn');
    if (headerSearchExitBtn) {
      headerSearchExitBtn.style.display = 'none';
    }

    // Clear search fields and live search results on close
    const headerInput = $('#header-search-input');
    const pageInput = $('#search-page-input');
    if (headerInput) {
      headerInput.value = '';
      headerInput.blur();
    }
    if (pageInput) {
      pageInput.value = '';
      pageInput.blur();
    }

    const liveResults = $('#search-page-live-results');
    if (liveResults) {
      liveResults.innerHTML = '';
      liveResults.style.display = 'none';
    }

    document.body.style.overflow = '';
  }
};

/* ---- Add to Cart (Product Page) ------------------------- */
function initProductPage() {
  const form = $('#product-form');
  if (!form) return;

  const addBtn = $('#add-to-cart-btn');
  const variantBtns = $$('.variant-btn, .variant-color-btn');
  let selectedVariantId = form.dataset.defaultVariant;

  function applyVariantChange(btn) {
    // Deactivate same type
    const isSizeBtn = btn.classList.contains('variant-btn');
    const isColorBtn = btn.classList.contains('variant-color-btn');
    if (isSizeBtn) $$('.variant-btn').forEach(b => b.classList.remove('is-active'));
    if (isColorBtn) $$('.variant-color-btn').forEach(b => b.classList.remove('is-active'));
    btn.classList.add('is-active');
    selectedVariantId = btn.dataset.variantId;

    // Update price — render main price and preserve slashed compare_at_price (strike-through)
    const priceEl = $('#product-price');
    if (priceEl) {
      const formattedPrice = btn.dataset.priceFormatted || (btn.dataset.price ? formatMoney(parseInt(btn.dataset.price, 10)) : '');
      const comparePrice = btn.dataset.compareAtPriceFormatted || '';

      if (comparePrice) {
        priceEl.innerHTML = `<span>${formattedPrice}</span><s style="margin-left:8px; opacity:0.5; font-weight:400;">${comparePrice}</s>`;
      } else {
        priceEl.innerHTML = `<span>${formattedPrice}</span>`;
      }
    }

    // Update availability — sold-out variants switch the button to "Notify me"
    const available = btn.dataset.available === 'true';
    if (addBtn) {
      if (available) {
        addBtn.textContent = 'ADD TO BAG';
        addBtn.disabled = false;
        addBtn.dataset.notify = '';
        addBtn.classList.remove('product-add-btn--notify');
      } else {
        addBtn.textContent = 'NOTIFY ME WHEN AVAILABLE';
        addBtn.disabled = false;
        addBtn.dataset.notify = 'true';
        addBtn.classList.add('product-add-btn--notify');
      }
    }

    // Update low stock
    const lowStockEl = $('#product-low-stock');
    if (lowStockEl) {
      try {
        const dataEl = $('#product-data');
        if (dataEl) {
          if (!window._productDataCache) {
            window._productDataCache = JSON.parse(dataEl.textContent);
          }
          const data = window._productDataCache;
          if (data.has_only_default_variant || (data.variants && data.variants.length <= 1 && data.options && data.options[0] === 'Title')) {
            lowStockEl.style.display = 'none';
          } else {
            const variant = data.variants.find(v => String(v.id) === String(selectedVariantId));
            let newText = '';
            if (variant && variant.available) {
              if (variant.inventory_quantity > 0 && variant.inventory_quantity <= 5) {
                newText = `Only ${variant.inventory_quantity} units left`;
              } else {
                newText = 'In stock';
              }
            } else {
              newText = 'Sold out';
            }

            if (lowStockEl.textContent !== newText) {
              lowStockEl.textContent = newText;
              lowStockEl.style.display = 'block';
            }
          }
        }
      } catch (_) { }
    }
  }

  // Clean up empty app block wrappers & empty Tailwind divs that contain only <br> or <script>/<style>
  function cleanupEmptyBlocks() {
    document.querySelectorAll('.product-app-block, div[class*="empty:hidden"], div[class*="min-h-"], .pl-swatches').forEach(el => {
      const clone = el.cloneNode(true);
      clone.querySelectorAll('script, style').forEach(s => s.remove());
      const visibleText = clone.textContent.replace(/[\n\r\s\u200b]/g, '');
      const visibleMedia = clone.querySelectorAll('img, svg, button, input, select, iframe, a, canvas, .pl-swatches__swatch');

      const hasSwatches = el.querySelector('.pl-swatches');
      const swatchesEmpty = hasSwatches ? (hasSwatches.children.length === 0 && !hasSwatches.textContent.trim()) : false;

      if ((!visibleText && visibleMedia.length === 0) || swatchesEmpty || (el.classList.contains('pl-swatches') && el.children.length === 0)) {
        const target = el.closest('.product-app-block') || el;
        target.style.setProperty('display', 'none', 'important');
        target.style.setProperty('margin-top', '0', 'important');
        target.style.setProperty('margin-bottom', '0', 'important');
        target.style.setProperty('margin', '0', 'important');
        target.style.setProperty('padding', '0', 'important');
        target.style.setProperty('height', '0', 'important');
        target.style.setProperty('min-height', '0', 'important');
      }
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', cleanupEmptyBlocks);
  } else {
    cleanupEmptyBlocks();
  }
  setTimeout(cleanupEmptyBlocks, 300);
  setTimeout(cleanupEmptyBlocks, 1000);

  // Format third-party app swatches in title-row on mobile (show active swatch + +N badge)
  let isFormattingSwatches = false;
  function formatTitleRowSwatches() {
    if (isFormattingSwatches || window.innerWidth > 767) return;
    isFormattingSwatches = true;
    try {
      document.querySelectorAll('.product-title-row .pl-swatches__container').forEach(container => {
        const links = Array.from(container.querySelectorAll('.pl-swatches__link'));
        if (links.length > 1) {
          let activeIdx = links.findIndex(link =>
            link.querySelector('.pl-swatches__swatch-wrapper--active') !== null ||
            link.getAttribute('aria-current') === 'page'
          );
          if (activeIdx === -1) activeIdx = 0;

          links.forEach((link, idx) => {
            if (idx !== activeIdx) {
              if (link.style.display !== 'none') {
                link.style.setProperty('display', 'none', 'important');
              }
            } else {
              if (link.style.display !== 'inline-flex') {
                link.style.setProperty('display', 'inline-flex', 'important');
              }
            }
          });

          let badge = container.querySelector('.pl-swatches-more-badge');
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'swatch-more-badge pl-swatches-more-badge';
            container.appendChild(badge);
          }
          const badgeText = '+' + (links.length - 1);
          if (badge.textContent !== badgeText) {
            badge.textContent = badgeText;
          }
        }
      });
    } catch (_) { }
    isFormattingSwatches = false;
  }

  const titleRowEl = document.querySelector('.product-title-row');
  if (titleRowEl) {
    const observer = new MutationObserver((mutations) => {
      const isOurMutation = mutations.some(m =>
        (m.target && m.target.classList && m.target.classList.contains('pl-swatches-more-badge')) ||
        (m.addedNodes && Array.from(m.addedNodes).some(n => n.classList && n.classList.contains('pl-swatches-more-badge')))
      );
      if (!isOurMutation && !isFormattingSwatches) {
        formatTitleRowSwatches();
      }
    });
    observer.observe(titleRowEl, { childList: true, subtree: true });
    formatTitleRowSwatches();
    setTimeout(formatTitleRowSwatches, 300);
    setTimeout(formatTitleRowSwatches, 1000);
  }

  // Color Options Bottom Sheet Handler
  const colorSheet = document.getElementById('color-options-sheet');
  const colorSheetBg = document.getElementById('color-sheet-bg');
  const colorSheetClose = document.getElementById('color-sheet-close');
  const colorSheetContent = document.getElementById('color-sheet-content');

  function openColorSheet() {
    if (!colorSheet || !colorSheetContent) return;
    colorSheetContent.innerHTML = '';

    // Check third-party app swatches first
    const appLinks = document.querySelectorAll('.product-title-row .pl-swatches__link, .product-aside .pl-swatches__link');
    if (appLinks.length > 0) {
      appLinks.forEach(link => {
        const title = link.getAttribute('title') || link.getAttribute('aria-label') || 'Color';
        const href = link.getAttribute('href') || 'javascript:void(0);';
        const colorEl = link.querySelector('.pl-swatches__color');
        const bg = colorEl ? (colorEl.style.background || colorEl.style.backgroundColor || '#000000') : '#000000';
        const isActive = link.querySelector('.pl-swatches__swatch-wrapper--active') !== null || link.getAttribute('aria-current') === 'page';
        const isWhite = /#ffffff|#fff|255,\s*255,\s*255|white/i.test(bg) || title.toLowerCase() === 'white';
        const borderCss = isWhite ? ' border: 1px solid #e8e8e8 !important;' : '';

        const item = document.createElement('a');
        item.className = 'color-sheet__item' + (isActive ? ' is-active' : '');
        item.href = href;
        item.innerHTML = `
          <span class="color-sheet__swatch" style="background: ${bg};${borderCss}"></span>
          <span class="color-sheet__name">${title}</span>
          <span class="color-sheet__badge">${isActive ? 'Active' : 'View'}</span>
        `;
        colorSheetContent.appendChild(item);
      });
    } else {
      // Theme variant swatches fallback
      const variantSwatches = document.querySelectorAll('.variant-color-btn');
      variantSwatches.forEach(btn => {
        const colorName = btn.dataset.color || btn.getAttribute('title') || 'Color';
        const bg = btn.style.background || btn.style.backgroundColor || '#000000';
        const isActive = btn.classList.contains('is-active');
        const isWhite = /#ffffff|#fff|255,\s*255,\s*255|white/i.test(bg) || colorName.toLowerCase() === 'white';
        const borderCss = isWhite ? ' border: 1px solid #e8e8e8 !important;' : '';

        const item = document.createElement('div');
        item.className = 'color-sheet__item' + (isActive ? ' is-active' : '');
        item.style.cursor = 'pointer';
        item.innerHTML = `
          <span class="color-sheet__swatch" style="background: ${bg};${borderCss}"></span>
          <span class="color-sheet__name">${colorName}</span>
          <span class="color-sheet__badge">${isActive ? 'Active' : 'Select'}</span>
        `;
        item.addEventListener('click', () => {
          btn.click();
          closeColorSheet();
        });
        colorSheetContent.appendChild(item);
      });
    }

    colorSheet.classList.add('is-open');
    colorSheet.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closeColorSheet() {
    if (!colorSheet) return;
    colorSheet.classList.remove('is-open');
    colorSheet.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  if (colorSheetBg) colorSheetBg.addEventListener('click', closeColorSheet);
  if (colorSheetClose) colorSheetClose.addEventListener('click', closeColorSheet);

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('.product-title-row .swatch-more-badge, .product-title-row .pl-swatches-more-badge, .product-title-row .pl-swatches__link, .product-title-row .variant-color-btn, .product-title-row .product-info__swatches');
    if (trigger && window.innerWidth <= 767) {
      e.preventDefault();
      e.stopPropagation();
      openColorSheet();
    }
  });

  // Variant selection
  variantBtns.forEach(btn => {
    btn.addEventListener('click', () => applyVariantChange(btn));
  });

  // Add to cart
  if (addBtn) {
    addBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      // Sold-out / notify mode: open the Get Notified popup instead of adding
      if (addBtn.dataset.notify === 'true') {
        if (window.openNotifyModal) window.openNotifyModal(selectedVariantId);
        return;
      }
      const btn = addBtn;
      const originalText = btn.textContent;
      btn.disabled = true;
      btn.innerHTML = '<span class="spinner"></span>';

      try {
        const addedItem = await Cart.add(selectedVariantId, 1);
        const cart = await Cart.get();
        updateCartCount(cart.item_count);
        btn.textContent = originalText;
        btn.disabled = false;
        if (AtbModal.el) {
          AtbModal.open(addedItem);
        } else {
          showToast('Added to bag');
          CartDrawer.refresh();
        }
      } catch (err) {
        btn.textContent = originalText;
        btn.disabled = false;
      }
    });
  }
}

/* ---- Quick Add (Collection / Homepage grids) ------------ */
async function quickAddVariant(variantId, btn) {
  if (!variantId) return;
  if (btn) btn.disabled = true;
  try {
    const addedItem = await Cart.add(variantId, 1);
    const cart = await Cart.get();
    updateCartCount(cart.item_count);
    if (btn) btn.disabled = false;
    if (AtbModal.el) {
      AtbModal.open(addedItem);
    } else {
      showToast('Added to bag');
    }
  } catch (err) {
    if (btn) btn.disabled = false;
  }
}

/* ---- Quick Add Modal (PC Modal / Mobile Bottom Sheet) ---- */
const QuickAddModal = {
  el: null,
  overlay: null,
  closeBtn: null,
  contentEl: null,
  productCache: {},

  init() {
    this.el = $('#quick-add-modal');
    this.overlay = $('#qa-overlay');
    this.closeBtn = $('#qa-close');
    this.contentEl = $('#qa-content');
    if (!this.el) return;

    this.closeBtn && this.closeBtn.addEventListener('click', () => this.close());
    this.overlay && this.overlay.addEventListener('click', () => this.close());

    this.el.addEventListener('click', (e) => {
      if (e.target.closest('#qa-close, .qa-modal__close')) {
        e.preventDefault();
        this.close();
      }
    });
  },

  openModal() {
    if (!this.el) return;
    this.el.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    document.documentElement.classList.add('is-qa-open');
    document.body.classList.add('is-qa-open');
  },

  close() {
    if (!this.el) return;
    this.el.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.documentElement.classList.remove('is-qa-open');
    document.body.classList.remove('is-qa-open');
  },

  async open(handle, cardEl) {
    this.openModal();
    if (!this.contentEl) return;

    this.contentEl.innerHTML = `
      <div class="qa-skeleton">
        <div class="skeleton-pulse" style="width: 55%; height: 16px; margin-bottom: 8px; background: rgba(0,0,0,0.06);"></div>
        <div class="skeleton-pulse" style="width: 25%; height: 13px; margin-bottom: 18px; background: rgba(0,0,0,0.06);"></div>
        <div class="qa-skeleton__gallery" style="display: flex; gap: 4px; margin-bottom: 20px;">
          <div class="skeleton-pulse" style="width: 115px; height: 172px; background: rgba(0,0,0,0.06); flex-shrink: 0;"></div>
          <div class="skeleton-pulse" style="width: 115px; height: 172px; background: rgba(0,0,0,0.06); flex-shrink: 0;"></div>
          <div class="skeleton-pulse" style="width: 115px; height: 172px; background: rgba(0,0,0,0.06); flex-shrink: 0;"></div>
        </div>
        <div class="skeleton-pulse" style="width: 35%; height: 11px; margin-bottom: 12px; background: rgba(0,0,0,0.06);"></div>
        <div style="display: flex; gap: 4px; margin-bottom: 24px;">
          <div class="skeleton-pulse" style="flex: 1; height: 35px; background: rgba(0,0,0,0.06);"></div>
          <div class="skeleton-pulse" style="flex: 1; height: 35px; background: rgba(0,0,0,0.06);"></div>
          <div class="skeleton-pulse" style="flex: 1; height: 35px; background: rgba(0,0,0,0.06);"></div>
          <div class="skeleton-pulse" style="flex: 1; height: 35px; background: rgba(0,0,0,0.06);"></div>
        </div>
        <div class="skeleton-pulse" style="width: 100%; height: 40px; background: rgba(0,0,0,0.06);"></div>
      </div>
    `;

    let productData = this.productCache[handle];
    if (!productData) {
      try {
        const res = await fetch(`/products/${handle}.js`);
        if (res.ok) {
          productData = await res.json();
          this.productCache[handle] = productData;
        }
      } catch (e) {
        console.error(e);
      }
    }

    if (!productData) {
      this.close();
      return;
    }

    // Build images gallery HTML
    const imagesHtml = (productData.images || []).map(img => `
      <img src="${img}" alt="${productData.title}" class="qa-modal__gallery-img" loading="lazy">
    `).join('');

    // Filter size variants
    let selectedVariantId = null;
    const variantsHtml = (productData.variants || []).map(v => {
      let sizeLabel = v.title;
      if (v.option2 && v.option2 !== 'Default Title') sizeLabel = v.option2;
      else if (v.option1 && v.option1 !== 'Default Title') sizeLabel = v.option1;

      if (v.available && !selectedVariantId) {
        selectedVariantId = v.id;
      }

      const isSelected = selectedVariantId === v.id;
      return `
        <button type="button" class="qa-modal__size-btn btn-reset ${isSelected ? 'is-selected' : ''}" data-variant-id="${v.id}" ${!v.available ? 'disabled' : ''}>
          ${sizeLabel}
        </button>
      `;
    }).join('');

    const isSingleDefault = (productData.variants || []).length === 1 && productData.variants[0].title === 'Default Title';

    this.contentEl.innerHTML = `
      <div class="qa-modal__head">
        <div>
          <h2 class="qa-modal__title"><a href="${productData.url || `/products/${handle}`}" class="qa-modal__title-link">${productData.title}</a></h2>
          <p class="qa-modal__price">${formatMoney(productData.price)}</p>
        </div>
      </div>

      ${imagesHtml ? `<div class="qa-modal__gallery">${imagesHtml}</div>` : ''}

      ${!isSingleDefault ? `
        <p class="qa-modal__size-label">Choose your size</p>
        <div class="qa-modal__sizes">
          ${variantsHtml}
        </div>
      ` : ''}

      <button type="button" class="qa-modal__add-btn btn-reset" id="qa-submit-btn" ${!selectedVariantId ? 'disabled' : ''}>
        ADD TO BAG
      </button>
    `;

    const inlineClose = $('#qa-close', this.contentEl);
    inlineClose && inlineClose.addEventListener('click', () => this.close());

    // Size selection handler
    const sizeBtns = $$('.qa-modal__size-btn', this.contentEl);
    const submitBtn = $('#qa-submit-btn', this.contentEl);

    sizeBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        sizeBtns.forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        selectedVariantId = btn.dataset.variantId;
        if (submitBtn) submitBtn.disabled = false;
      });
    });

    // Add to bag click
    if (submitBtn) {
      submitBtn.addEventListener('click', async () => {
        if (!selectedVariantId) return;
        submitBtn.disabled = true;
        submitBtn.textContent = 'ADDING...';

        try {
          const addedItem = await Cart.add(selectedVariantId, 1);
          const cart = await Cart.get();
          updateCartCount(cart.item_count);
          this.close();

          if (window.innerWidth < 768) {
            CartDrawer.open();
            CartDrawer.render(cart);
          } else {
            AtbModal.open(addedItem);
          }
        } catch (e) {
          console.error(e);
          submitBtn.disabled = false;
          submitBtn.textContent = 'ADD TO BAG';
        }
      });
    }
  }
};

function initQuickAdd() {
  QuickAddModal.init();

  // Mobile: "+" icon always opens QuickAddModal bottom sheet
  document.addEventListener('click', (e) => {
    const quickAddBtn = e.target.closest('.product-card__quick-add');
    if (!quickAddBtn) return;
    if (window.innerWidth >= 768) return;
    e.preventDefault();
    e.stopPropagation();

    const card = quickAddBtn.closest('.product-card');
    if (!card) return;
    const handle = card.dataset.handle;
    if (handle) {
      QuickAddModal.open(handle, card);
    }
  });

  // Desktop: Hover size bar button click adds variant directly
  document.addEventListener('click', async (e) => {
    const sizeBtn = e.target.closest('.product-card__size-btn');
    if (!sizeBtn || sizeBtn.closest('#quick-add-modal')) return;
    const variantId = sizeBtn.dataset.addVariant;
    if (!variantId) return;

    e.preventDefault();
    e.stopPropagation();

    sizeBtn.style.opacity = '0.5';
    try {
      const addedItem = await Cart.add(variantId, 1);
      const cart = await Cart.get();
      updateCartCount(cart.item_count);
      sizeBtn.style.opacity = '1';

      if (window.innerWidth < 768) {
        CartDrawer.open();
        CartDrawer.render(cart);
      } else {
        AtbModal.open(addedItem);
      }
    } catch (err) {
      console.error(err);
      sizeBtn.style.opacity = '1';
    }
  });
}

/* ---- Cart Page ------------------------------------------ */
function initCartPage() {
  const cartPage = $('.cart-page');
  if (!cartPage) return;

  const backBtn = $('.cart-page .cart-header__back, main .cart-header__back');

  if (backBtn && !backBtn.closest('#cart-drawer')) {
    backBtn.removeAttribute('onclick');
    backBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const ref = document.referrer;
      if (ref && !ref.includes('/cart')) {
        window.location.href = ref;
      } else if (history.length > 1) {
        history.back();
      } else {
        window.location.href = '/';
      }
    });
  }

  if (!cartPage) return;

  // Tab switching
  function switchTab(target, updateUrl = true) {
    const activeBtn = $$('.cart-tab-btn').find(b => b.dataset.tab === target);
    if (!activeBtn) return;

    $$('.cart-tab-btn').forEach(b => b.classList.remove('is-active'));
    activeBtn.classList.add('is-active');

    $$('[data-tab-panel]').forEach(p => {
      p.style.display = p.dataset.tabPanel === target ? '' : 'none';
    });

    const cartSuggested = $('.cart-suggested');
    if (cartSuggested) {
      cartSuggested.style.display = target === 'bag' ? '' : 'none';
    }

    if (updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('view', target);
      window.history.replaceState({}, '', url.toString());
    }
  }

  $$('.cart-tab-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const target = btn.dataset.tab;
      switchTab(target, true);
    });
  });

  // Check URL params on load to restore tab state (e.g. /cart?view=looks)
  const urlParams = new URLSearchParams(window.location.search);
  const viewParam = urlParams.get('view');
  if (viewParam && (viewParam === 'looks' || viewParam === 'bag')) {
    switchTab(viewParam, false);
  }

  // Quantity controls
  $$('.qty-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const variantId = btn.dataset.variant;
      const action = btn.dataset.action;
      const display = btn.parentElement.querySelector('.qty-display');
      let qty = parseInt(display?.textContent || '1', 10);
      qty = action === 'increase' ? qty + 1 : Math.max(0, qty - 1);

      try {
        const cart = await Cart.change(variantId, qty);
        updateCartCount(cart.item_count);
        // Reload page to reflect server-rendered cart state
        window.location.reload();
      } catch (err) { }
    });
  });

  // Remove buttons
  $$('[data-remove]').forEach(btn => {
    btn.addEventListener('click', async () => {
      try {
        const cart = await Cart.change(btn.dataset.remove, 0);
        updateCartCount(cart.item_count);
        window.location.reload();
      } catch (err) { }
    });
  });

  // Size dropdown: swap the line to the chosen size's variant (remove old + add new)
  $$('.cart-item__size').forEach(sel => {
    sel.addEventListener('change', async () => {
      const oldVariant = sel.dataset.lineVariant;
      const newVariant = sel.value;
      const qty = parseInt(sel.dataset.qty || '1', 10);
      if (!newVariant || newVariant === oldVariant) return;
      try {
        await Cart.change(oldVariant, 0);
        await Cart.add(newVariant, qty);
      } catch (err) { }
      window.location.reload();
    });
  });
}

/* ---- Collection Filter ---------------------------------- */
function initCollectionFilter() {
  const filterToggle = $('#filter-toggle');
  const filterBar = $('#filter-bar');
  if (!filterToggle || !filterBar) return;

  filterToggle.addEventListener('click', () => {
    filterBar.classList.toggle('is-open');
  });

  $$('.filter-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      $$('.filter-btn').forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      const sortVal = btn.dataset.sort;
      sortProducts(sortVal);
    });
  });
}

/* ---- Grid view toggle (collection page: cycle 4 / 2 / 1 columns) ---- */
function initGridView() {
  const toggle = $('#grid-view-toggle');
  const grid = $('#collection-grid');
  if (!grid) return;

  const isDesktop = window.matchMedia('(min-width: 768px)').matches;
  const views = isDesktop ? [4, 2] : [3, 2, 1];
  let idx = 0;
  try {
    const saved = parseInt(localStorage.getItem('bluer-grid-view') || (isDesktop ? '4' : '3'), 10);
    if (views.indexOf(saved) !== -1) idx = views.indexOf(saved);
  } catch (e) { }

  function updateDynamicSpans() {
    if (window.matchMedia('(min-width: 768px)').matches) return;
    const cards = Array.from(grid.querySelectorAll('.product-card'));
    cards.forEach((card, i) => {
      card.style.gridColumn = '';
    });
    if (!grid.classList.contains('gv-3') && grid.className.indexOf('gv-') !== -1) return;
    cards.forEach((card, i) => {
      const num = i + 1;
      if (num % 3 === 0) {
        card.style.gridColumn = 'span 2';
      } else if (num % 3 === 1 && i === cards.length - 1) {
        card.style.gridColumn = 'span 2';
      } else {
        card.style.gridColumn = 'span 1';
      }
    });
  }

  function apply(v) {
    grid.classList.remove('gv-4', 'gv-3', 'gv-2', 'gv-1');
    grid.classList.add('gv-' + v);
    updateDynamicSpans();
    if (toggle) {
      toggle.dataset.view = String(v);
      const activeViews = views;
      const currentIdx = activeViews.indexOf(v);
      const nextVal = activeViews[currentIdx === -1 ? 0 : (currentIdx + 1) % activeViews.length];
      $$('.gvi', toggle).forEach(ic => {
        ic.style.display = ic.dataset.for === String(nextVal) ? 'block' : 'none';
      });
    }
    try { localStorage.setItem('bluer-grid-view', String(v)); } catch (e) { }
  }
  apply(views[idx]);

  if (toggle) {
    toggle.addEventListener('click', () => {
      idx = (idx + 1) % views.length;
      apply(views[idx]);
    });
  }
}

function sortProducts(sort) {
  const grid = $('#collection-grid');
  if (!grid) return;
  const cards = $$('.product-card', grid);
  cards.sort((a, b) => {
    if (sort === 'price-asc') return getPriceCents(a) - getPriceCents(b);
    if (sort === 'price-desc') return getPriceCents(b) - getPriceCents(a);
    return parseInt(a.dataset.index || '0', 10) - parseInt(b.dataset.index || '0', 10);
  });
  cards.forEach(c => grid.appendChild(c));
}

function getPriceCents(card) {
  return parseInt(card.dataset.price || '0', 10);
}

/* ---- Newsletter ----------------------------------------- */
function initNewsletter() {
  $$('.footer-email-form').forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const input = form.querySelector('input[type="email"]');
      const btn = form.querySelector('button[type="submit"]');
      if (!input || !btn) return;
      const orig = btn.textContent;
      btn.textContent = '...';
      btn.disabled = true;
      // Shopify handles newsletter via form POST to /contact#contact_form
      // For now show success message
      setTimeout(() => {
        input.value = '';
        btn.textContent = 'Done';
        setTimeout(() => { btn.textContent = orig; btn.disabled = false; }, 2000);
      }, 600);
    });
  });
}

/* ---- Header search → dedicated search page -------------- */
/* Per design: clicking SEARCH opens the full search page (recommendations +
   featured below), rather than searching inline from the header. */
/* Fade the current page out, then navigate — gives a blend into the next page */
function navigateWithBlend(url) {
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    window.location.href = url;
    return;
  }
  document.documentElement.classList.add('page-leaving');
  setTimeout(() => { window.location.href = url; }, 200);
}

function initHeaderSearch() {
  const input = $('#header-search-input');
  if (!input) return;
  function go(e) {
    if (location.pathname.indexOf('/search') === 0) return;
    e.preventDefault();
    if (typeof SearchModal !== 'undefined') {
      SearchModal.open();
    }
  }
  input.addEventListener('click', go);
}

/* Search page form: type + Enter blends out, then loads the results page */
function initSearchPageForm() {
  const input = $('#search-page-input');
  if (!input) return;

  // Only auto-focus when actually navigating to the standalone /search page URL
  if (location.pathname.indexOf('/search') === 0) {
    const forceFocus = () => {
      if (document.activeElement !== input) {
        input.focus();
      }
    };
    forceFocus();
    setTimeout(forceFocus, 100);
  }

  const form = input.closest('form');
  if (!form) return;
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const q = input.value.trim();
    if (!q) { input.focus(); return; }
    if (location.pathname.indexOf('/search') === 0) {
      const sep = form.action.indexOf('?') > -1 ? '&' : '?';
      navigateWithBlend(form.action + sep + 'q=' + encodeURIComponent(q) + '&type=product');
    }
  });
}

/* Live predictive search autocomplete dropdown (search-as-you-type) */
function initPredictiveSearch() {
  const isSearchPage = location.pathname.indexOf('/search') === 0 || !!document.getElementById('search-modal-container');

  const inputs = [$('#header-search-input'), $('#search-page-input')].filter(Boolean);
  inputs.forEach(input => {
    const parent = input.parentElement;
    if (!parent) return;

    let resultsContainer = document.getElementById('search-page-live-results');
    if (!resultsContainer) {
      resultsContainer = $('.predictive-search-results', parent);
      if (!resultsContainer) {
        resultsContainer = document.createElement('div');
        resultsContainer.className = 'predictive-search-results';
        resultsContainer.style.display = 'none';
        parent.appendChild(resultsContainer);
      }
    }

    let debounceTimer;
    let abortController = null;
    let activeQuery = '';

    const headerSearchCloseBtn = $('#header-search-close-btn');
    const pageClearBtn = $('#search-clear-btn');

    const updateClearBtnsVisibility = (val) => {
      const show = val.length > 0 ? 'block' : 'none';
      if (headerSearchCloseBtn) headerSearchCloseBtn.style.display = show;
      if (pageClearBtn) pageClearBtn.style.display = show;
    };

    // Initial check on load
    if (input.value) {
      updateClearBtnsVisibility(input.value);
    }

    const clearAllSearchInputs = (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      clearTimeout(debounceTimer);
      if (abortController) {
        abortController.abort();
        abortController = null;
      }
      activeQuery = '';
      const hInp = $('#header-search-input');
      const pInp = $('#search-page-input');
      if (hInp) hInp.value = '';
      if (pInp) pInp.value = '';
      updateClearBtnsVisibility('');
      document.documentElement.classList.remove('search-is-typing');
      if (resultsContainer) {
        resultsContainer.innerHTML = '';
        resultsContainer.style.display = 'none';
      }
      if (input) input.focus();
    };

    if (headerSearchCloseBtn && !headerSearchCloseBtn.hasAttribute('data-clear-bound')) {
      headerSearchCloseBtn.setAttribute('data-clear-bound', 'true');
      headerSearchCloseBtn.addEventListener('click', clearAllSearchInputs);
    }
    if (pageClearBtn && !pageClearBtn.hasAttribute('data-clear-bound')) {
      pageClearBtn.setAttribute('data-clear-bound', 'true');
      pageClearBtn.addEventListener('click', clearAllSearchInputs);
    }

    input.addEventListener('search', () => {
      if (!input.value.trim()) {
        clearAllSearchInputs();
      }
    });

    input.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      const query = input.value.trim();
      updateClearBtnsVisibility(input.value);

      if (query.length < 2) {
        if (abortController) {
          abortController.abort();
          abortController = null;
        }
        activeQuery = '';
        document.documentElement.classList.remove('search-is-typing');
        resultsContainer.innerHTML = '';
        resultsContainer.style.display = 'none';
        return;
      }

      document.documentElement.classList.add('search-is-typing');
      resultsContainer.style.display = 'block';

      // Show skeleton loading cards
      if (!resultsContainer.innerHTML || resultsContainer.innerHTML.includes('predictive-search-empty')) {
        resultsContainer.innerHTML = `
          <div class="product-grid search-desktop-results" id="collection-grid">
            ${Array(6).fill(0).map(() => `
              <div class="product-card">
                <div class="product-card__img-wrap" style="aspect-ratio: 2/3; background: #f0f0f0; animation: skeleton-pulse 1.2s infinite ease-in-out;"></div>
                <div class="product-card__info" style="padding: 8px 0 14px;">
                  <div class="search-skeleton-line" style="width: 70%; height: 10px; background: #f5f5f5; animation: skeleton-pulse 1.2s infinite ease-in-out; margin-bottom: 8px;"></div>
                  <div class="search-skeleton-line" style="width: 40%; height: 10px; background: #f5f5f5; animation: skeleton-pulse 1.2s infinite ease-in-out;"></div>
                </div>
              </div>
            `).join('')}
          </div>
          <div class="search-skeleton search-mobile-results">
            ${Array(6).fill(0).map(() => `
              <div class="search-skeleton-item">
                <div class="search-skeleton-img"></div>
                <div class="search-skeleton-info">
                  <div class="search-skeleton-line search-skeleton-title"></div>
                  <div class="search-skeleton-line search-skeleton-price"></div>
                </div>
              </div>
            `).join('')}
          </div>
        `;
      }

      debounceTimer = setTimeout(() => {
        if (abortController) {
          abortController.abort();
        }
        abortController = new AbortController();
        const signal = abortController.signal;
        activeQuery = query;

        fetch(`/search?view=ajax&q=${encodeURIComponent(query)}*&type=product&options[prefix]=last`, { signal })
          .then(res => res.json())
          .then(products => {
            if (activeQuery !== query) return;
            if ((!products || products.length === 0) && query.includes(' ')) {
              const firstWord = query.split(' ')[0];
              return fetch(`/search?view=ajax&q=${encodeURIComponent(firstWord)}*&type=product&options[prefix]=last`, { signal })
                .then(res => res.json());
            }
            return products;
          })
          .then(products => {
            if (activeQuery !== query || !products) return;

            if (products.length === 0) {
              resultsContainer.innerHTML = '<div class="predictive-search-empty">No products found</div>';
              resultsContainer.style.display = 'block';
              return;
            }

            if (isSearchPage) {
              let gridHtml = '<div class="product-grid search-desktop-results" id="collection-grid">';
              products.forEach(p => {
                const priceText = p.price ? formatMoney(parseFloat(p.price) * 100) : '';
                gridHtml += `
                  <div class="product-card">
                    <a href="${p.url}" class="product-card__img-wrap" style="aspect-ratio: 2/3;">
                      ${p.image ? `<img class="product-card__img" src="${p.image}" alt="${p.title}" loading="lazy">` : '<div class="product-card__img" style="background:var(--color-muted)"></div>'}
                    </a>
                    <div class="product-card__info">
                      <p class="product-card__title">
                        <a href="${p.url}">${p.title}</a>
                      </p>
                      <p class="product-card__price">${priceText}</p>
                    </div>
                  </div>
                `;
              });
              gridHtml += '</div>';

              let listHtml = '<div class="search-mobile-results">';
              products.forEach(p => {
                const priceText = p.price ? formatMoney(parseFloat(p.price) * 100) : '';
                listHtml += `
                  <a href="${p.url}" class="predictive-search-item">
                    ${p.image ? `<img class="predictive-search-img" src="${p.image}" alt="${p.title}">` : '<div class="predictive-search-img"></div>'}
                    <div class="predictive-search-info">
                      <span class="predictive-search-title">${p.title}</span>
                      <span class="predictive-search-price">${priceText}</span>
                    </div>
                  </a>
                `;
              });
              listHtml += '</div>';

              resultsContainer.innerHTML = gridHtml + listHtml;
            } else {
              resultsContainer.innerHTML = products.map(p => {
                const priceText = p.price ? formatMoney(parseFloat(p.price) * 100) : '';
                return `
                  <a href="${p.url}" class="predictive-search-item">
                    ${p.image ? `<img class="predictive-search-img" src="${p.image}" alt="${p.title}">` : '<div class="predictive-search-img"></div>'}
                    <div class="predictive-search-info">
                      <span class="predictive-search-title">${p.title}</span>
                      <span class="predictive-search-price">${priceText}</span>
                    </div>
                  </a>
                `;
              }).join('');
            }
            resultsContainer.style.display = 'block';
          })
          .catch((err) => {
            if (err.name === 'AbortError') return;
            resultsContainer.style.display = 'none';
          });
      }, 300);
    });

    // Close dropdown on click outside
    document.addEventListener('click', (e) => {
      if (!parent.contains(e.target)) {
        resultsContainer.style.display = 'none';
      }
    });

    // Close dropdown on Escape
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        resultsContainer.style.display = 'none';
        input.blur();
      }
    });
  });
}

/* ---- Cart page open drawer link ------------------------- */
function initCartTrigger() {
  $$('[data-open-cart]').forEach(el => {
    el.addEventListener('click', (e) => {
      e.preventDefault();
      CartDrawer.open();
    });
  });
}

/* ---- Bootstrap ------------------------------------------ */
/* ============================================================
   PRODUCT IMAGE GALLERY (mobile carousel dots)
   ============================================================ */
function initProductGallery() {
  const track = $('#product-gallery-track');
  const dotsWrap = $('#product-gallery-dots');
  if (!track || !dotsWrap) return;
  const dots = $$('.product-gallery__dot', dotsWrap);
  if (!dots.length) return;

  let ticking = false;
  function update() {
    ticking = false;
    const i = Math.round(track.scrollLeft / track.clientWidth);
    dots.forEach((d, idx) => d.classList.toggle('is-active', idx === i));
  }
  track.addEventListener('scroll', () => {
    if (!ticking) { ticking = true; requestAnimationFrame(update); }
  }, { passive: true });
}

/* Full-screen image zoom (click a product image to open, click to zoom in/out) */
function initProductZoom() {
  const imgs = $$('.product-gallery__img');
  if (!imgs.length) return;

  const overlay = document.createElement('div');
  overlay.className = 'product-zoom';
  overlay.setAttribute('aria-hidden', 'true');
  overlay.innerHTML =
    '<button class="product-zoom__close btn-reset" aria-label="Close">&times;</button>' +
    '<div class="product-zoom__stage" id="product-zoom-stage"></div>';
  document.body.appendChild(overlay);

  const stage = overlay.querySelector('#product-zoom-stage');
  const closeBtn = overlay.querySelector('.product-zoom__close');

  let activeImg = null;
  let isDragging = false;
  let startX = 0, startY = 0;
  let currentX = 0, currentY = 0;
  let zoomLevel = 1.5; // 1.5 (default open), 3.5 (high zoom), 1.0 (zoomed out)
  let initialPinchDist = 0;
  let initialZoomLevel = 1.5;

  function openAt(src) {
    stage.innerHTML = '';
    const img = document.createElement('img');
    img.src = src;
    img.className = 'product-zoom__img is-zoomed';
    activeImg = img;
    currentX = 0;
    currentY = 0;
    zoomLevel = 1.5;
    img.style.transform = 'scale(1.5) translate(0px, 0px)'; // Zoomed by default

    // Click/tap to cycle zoom state: 1.5 -> 3.5 -> 1.0 -> 1.5...
    img.addEventListener('click', (e) => {
      if (e.defaultPrevented) return;

      if (zoomLevel === 1.5) {
        zoomLevel = 3.5;
        img.classList.add('is-zoomed');
        img.style.transform = `scale(3.5) translate(${currentX / 3.5}px, ${currentY / 3.5}px)`;
      } else if (zoomLevel === 3.5) {
        zoomLevel = 1.0;
        img.classList.remove('is-zoomed');
        img.style.transform = '';
      } else {
        zoomLevel = 1.5;
        img.classList.add('is-zoomed');
        currentX = 0;
        currentY = 0;
        img.style.transform = `scale(1.5) translate(0px, 0px)`;
      }
    });

    // Touch & Mouse drag-to-pan / pinch-to-zoom handlers
    const startDrag = (e) => {
      if (e.touches && e.touches.length === 2) {
        isDragging = false;
        initialPinchDist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
        initialZoomLevel = zoomLevel;
        img.style.transition = 'none';
        return;
      }

      if (zoomLevel === 1.0) return; // Don't drag if zoomed out
      isDragging = true;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      startX = clientX - currentX;
      startY = clientY - currentY;
      img.style.transition = 'none'; // Smooth dragging with no transition delay
    };

    const drag = (e) => {
      if (e.touches && e.touches.length === 2) {
        e.preventDefault();
        const dist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
        if (initialPinchDist > 0) {
          const factor = dist / initialPinchDist;
          zoomLevel = Math.max(1.0, Math.min(4.0, initialZoomLevel * factor));
          img.style.transform = `scale(${zoomLevel}) translate(${currentX / zoomLevel}px, ${currentY / zoomLevel}px)`;
        }
        return;
      }

      if (!isDragging) return;
      e.preventDefault();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      currentX = clientX - startX;
      currentY = clientY - startY;

      // Limit panning bounds to keep image on screen
      const maxDrag = zoomLevel === 1.0 ? 0 : (zoomLevel >= 3.0 ? 400 : 250);
      currentX = Math.max(-maxDrag, Math.min(maxDrag, currentX));
      currentY = Math.max(-maxDrag, Math.min(maxDrag, currentY));

      img.style.transform = `scale(${zoomLevel}) translate(${currentX / zoomLevel}px, ${currentY / zoomLevel}px)`;
    };

    const endDrag = () => {
      isDragging = false;
      initialPinchDist = 0;
      img.style.transition = 'transform 0.25s ease';
      if (zoomLevel > 1.05) {
        img.classList.add('is-zoomed');
      } else {
        img.classList.remove('is-zoomed');
        img.style.transform = '';
      }
    };

    // Mouse drag events
    img.addEventListener('mousedown', startDrag);
    window.addEventListener('mousemove', drag);
    window.addEventListener('mouseup', endDrag);

    // Touch drag events
    img.addEventListener('touchstart', startDrag, { passive: false });
    img.addEventListener('touchmove', drag, { passive: false });
    img.addEventListener('touchend', endDrag);

    stage.appendChild(img);
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function close() {
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    activeImg = null;
  }

  // On desktop, clicking an image opens zoom; on mobile, only the expand icon triggers it
  imgs.forEach((im) => {
    im.addEventListener('click', () => {
      if (window.innerWidth >= 768) {
        openAt(im.currentSrc || im.src);
      }
    });
  });

  // Expand icon button: opens zoom for currently active image in carousel
  const expandBtn = $('#product-gallery-expand');
  if (expandBtn) {
    expandBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const trackEl = $('#product-gallery-track');
      let targetImg = null;
      if (trackEl && trackEl.clientWidth > 0) {
        const i = Math.round(trackEl.scrollLeft / trackEl.clientWidth);
        const cell = trackEl.children[i] || trackEl.children[0];
        if (cell) targetImg = cell.querySelector('.product-gallery__img');
      }
      if (!targetImg && imgs.length > 0) {
        targetImg = imgs[0];
      }
      if (targetImg) openAt(targetImg.currentSrc || targetImg.src);
    });
  }

  closeBtn.addEventListener('click', close);
  overlay.addEventListener('click', (e) => { if (e.target === overlay || e.target === stage) close(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
}

/* ---- Product top bar: back + native share -------------------------- */
function initProductTopbar() {
  const menuBtn = $('#product-menu-btn');
  if (menuBtn) {
    menuBtn.addEventListener('click', (e) => {
      e.preventDefault();
      // Talk to NavDrawer directly. The old version checked window.NavDrawer
      // (never defined — NavDrawer is a module-scope const, not on window) and
      // called .toggle() (never existed — it has open() and close()), so it
      // always fell through to synthesising a click on the hidden header
      // button. That worked, but it is fragile and depends on the header
      // existing on a template where it is display:none.
      const drawer = $('#nav-drawer');
      if (!drawer) return;
      drawer.classList.contains('is-open') ? NavDrawer.close() : NavDrawer.open();
    });
  }

  document.body.addEventListener('click', async (e) => {
    const btn = e.target.closest('#product-share-btn, #product-title-share-btn');
    if (!btn) return;
    e.preventDefault();
    const url = btn.dataset.shareUrl || window.location.href;
    const title = btn.dataset.shareTitle || document.title;
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch (_) { }
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(url);
        showToast('Link copied to clipboard');
      } catch (_) { }
    } else {
      showToast('Link copied');
    }
  });
}

function initProductCardGalleries() {
  document.querySelectorAll('.product-card__gallery').forEach(gallery => {
    const dots = gallery.parentElement.querySelectorAll('.product-card__dot');
    if (!dots.length) return;

    let ticking = false;
    gallery.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const index = Math.round(gallery.scrollLeft / gallery.clientWidth);
          dots.forEach((dot, idx) => {
            dot.classList.toggle('is-active', idx === index);
          });
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  });
}

function initProductCardHoverGallery() {
  // Mousemove image swapping completely disabled per design requirement
  return;
}

document.addEventListener('DOMContentLoaded', () => {
  if (!window.location.pathname.includes('/cart') && !window.location.pathname.includes('/products/')) {
    try { sessionStorage.setItem('prev_storefront_page', window.location.href); } catch (_) { }
  }
  CartDrawer.init();
  NavDrawer.init();
  SearchModal.init();
  initProductPage();
  initProductGallery();
  initProductZoom();
  initQuickAdd();
  initProductCardGalleries();
  // initProductCardHoverGallery();
  initCartPage();
  initCollectionFilter();
  initGridView();
  initNewsletter();
  initHeaderSearch();
  initSearchPageForm();
  initPredictiveSearch();
  initCartTrigger();
  initFilterDrawer();
  initProductAccordions();
  initSizeGuideModal();
  initNotifyModal();
  initAtbModal();
  initMobileNav();
  initPincodeChecker();
  initProductTopbar();

  // Initialise cart count from server
  Cart.get().then(cart => updateCartCount(cart.item_count));
});

/* ============================================================
   FILTER DRAWER
   ============================================================ */
function initFilterDrawer() {
  const drawer = $('#filter-drawer');
  const toggles = $$('#filter-toggle, .filter-toggle-btn');
  const close = $('#filter-close');
  const overlay = $('#filter-overlay');
  const showItems = $('#filter-show-items');
  const clearAll = $('#filter-clear');
  if (!drawer) return;

  function openDrawer() {
    drawer.setAttribute('aria-hidden', 'false');
    toggles.forEach(t => t.setAttribute('aria-expanded', 'true'));
    document.body.style.overflow = 'hidden';
    document.documentElement.classList.add('is-filter-open');
  }

  function closeDrawer() {
    drawer.setAttribute('aria-hidden', 'true');
    toggles.forEach(t => t.setAttribute('aria-expanded', 'false'));
    document.body.style.overflow = '';
    document.documentElement.classList.remove('is-filter-open');
  }

  toggles.forEach(t => t.addEventListener('click', openDrawer));
  close && close.addEventListener('click', closeDrawer);
  overlay && overlay.addEventListener('click', closeDrawer);

  // Swipe right to close filter drawer on mobile (matches CartDrawer 1:1)
  let touchStartX = 0;
  let touchStartY = 0;
  const panel = $('.filter-drawer__panel', drawer) || drawer;

  panel.addEventListener('touchstart', (e) => {
    if (e.touches && e.touches.length === 1) {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }
  }, { passive: true });

  panel.addEventListener('touchend', (e) => {
    if (e.changedTouches && e.changedTouches.length === 1) {
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      const deltaX = touchEndX - touchStartX;
      const deltaY = touchEndY - touchStartY;

      // Swiping right to close drawer
      if (deltaX > 50 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
        closeDrawer();
      }
    }
  }, { passive: true });

  // Helper to build URL with currently active size and color filters
  function getFilteredUrl() {
    const params = new URLSearchParams(window.location.search);
    // Remove all existing filter params
    for (const key of Array.from(params.keys())) {
      if (key.startsWith('filter.')) {
        params.delete(key);
      }
    }
    // Add active size & color filters
    $$('.filter-size-btn.is-active, .filter-color-btn.is-active').forEach(btn => {
      const name = btn.getAttribute('data-param-name');
      const val = btn.getAttribute('data-filter-value');
      if (name && val) {
        params.append(name, val);
      }
    });
    const query = params.toString();
    return window.location.pathname + (query ? '?' + query : '');
  }

  // Update "SHOW ITEMS" count dynamically via AJAX background fetch
  async function updateFilterCount() {
    if (!showItems) return;
    const targetUrl = getFilteredUrl();
    showItems.textContent = 'Updating...';
    try {
      const res = await fetch(targetUrl);
      if (!res.ok) throw new Error('Fetch failed');
      const htmlText = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlText, 'text/html');
      const newShowBtn = doc.querySelector('#filter-show-items');
      if (newShowBtn) {
        showItems.textContent = newShowBtn.textContent.trim();
      } else {
        showItems.textContent = 'SHOW ITEMS';
      }
    } catch (e) {
      console.error(e);
      showItems.textContent = 'SHOW ITEMS';
    }
  }

  // Size toggle
  $$('.filter-size-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      btn.classList.toggle('is-active');
      await updateFilterCount();
    });
  });

  // Color toggle
  $$('.filter-color-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      btn.classList.toggle('is-active');
      await updateFilterCount();
    });
  });

  // Highlight active filters on page load
  const currentParams = new URLSearchParams(window.location.search);
  $$('.filter-size-btn, .filter-color-btn').forEach(btn => {
    const name = btn.getAttribute('data-param-name');
    const val = btn.getAttribute('data-filter-value');
    if (name && val && currentParams.getAll(name).includes(val)) {
      btn.classList.add('is-active');
    }
  });

  // Clear all
  clearAll && clearAll.addEventListener('click', async () => {
    $$('.filter-size-btn.is-active, .filter-color-btn.is-active').forEach(b => b.classList.remove('is-active'));
    await updateFilterCount();
  });

  // Show items — redirect to the filtered URL
  showItems && showItems.addEventListener('click', (e) => {
    e.preventDefault();
    closeDrawer();
    const targetUrl = getFilteredUrl();
    navigateWithBlend(targetUrl);
  });
}

/* ============================================================
   PRODUCT ACCORDIONS
   ============================================================ */
function initProductAccordions() {
  $$('.product-accordion__trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const expanded = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', String(!expanded));
      const body = trigger.nextElementSibling;
      if (body) body.classList.toggle('is-open', !expanded);
    });
  });
}

/* ============================================================
   SIZE GUIDE MODAL
   ============================================================ */
function initSizeGuideModal() {
  const modal = $('#size-chart-modal');
  const btn = $('#size-guide-btn');
  if (!modal) return;

  function open() {
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        modal.classList.add('is-active');
      });
    });
  }
  function closeFn() {
    modal.classList.remove('is-active');
    setTimeout(() => {
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }, 400);
  }

  btn && btn.addEventListener('click', open);
  $$('[data-size-chart-close]').forEach((el) => el.addEventListener('click', closeFn));
}

/* ============================================================
   NOTIFY ME MODAL
   ============================================================ */
function initNotifyModal() {
  const modal = $('#notify-modal');
  const btn = $('#notify-me-btn');
  const close = $('#notify-close');
  const bg = $('#notify-bg');
  const submit = $('#notify-submit');
  const sizeSelect = $('#notify-size');
  const productEl = $('#notify-product');
  if (!modal) return;

  function open(preselectVariantId) {
    // Product name (design shows the product title above the size dropdown)
    const form = $('#product-form');
    if (productEl) productEl.textContent = form ? (form.dataset.productTitle || '') : '';

    // Populate size options — only sold-out variants
    if (sizeSelect) {
      sizeSelect.innerHTML = '';
      $$('.variant-btn').forEach(vBtn => {
        if (vBtn.dataset.available === 'true') return; // skip in-stock sizes
        const opt = document.createElement('option');
        opt.value = vBtn.dataset.variantId;
        opt.textContent = vBtn.textContent.trim();
        if (preselectVariantId && String(vBtn.dataset.variantId) === String(preselectVariantId)) opt.selected = true;
        sizeSelect.appendChild(opt);
      });
    }
    if (submit) { submit.textContent = 'GET NOTIFIED'; submit.disabled = false; }
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }
  function closeFn() { modal.setAttribute('aria-hidden', 'true'); document.body.style.overflow = ''; }

  // Expose so the product page (sold-out / variant-change) can open it
  window.openNotifyModal = open;

  btn && btn.addEventListener('click', () => open());
  close && close.addEventListener('click', closeFn);
  bg && bg.addEventListener('click', closeFn);

  submit && submit.addEventListener('click', async () => {
    const email = $('#notify-email');
    if (!email || !email.value || !email.checkValidity()) {
      if (email) email.focus();
      return;
    }

    // Which product / size the shopper wants back in stock
    const productTitle = productEl ? productEl.textContent.trim() : '';
    const sizeText = (sizeSelect && sizeSelect.options.length)
      ? sizeSelect.options[sizeSelect.selectedIndex].textContent.replace(' — Sold out', '').trim()
      : '';
    const productUrl = window.location.href;

    submit.disabled = true;
    submit.textContent = 'Sending…';

    try {
      // Post to Shopify's native contact form — emails the store's contact address
      // with the shopper's email + the product/size they want notified about.
      const body = new FormData();
      body.append('form_type', 'contact');
      body.append('utf8', '✓');
      body.append('contact[email]', email.value);
      body.append('contact[body]',
        'Back-in-stock request\nProduct: ' + productTitle +
        '\nSize: ' + sizeText +
        '\nPage: ' + productUrl);
      const res = await fetch('/contact', { method: 'POST', body });
      if (!res.ok && res.type !== 'opaqueredirect') throw new Error('contact failed');
      submit.textContent = 'Done!';
      setTimeout(() => closeFn(), 1500);
    } catch (err) {
      submit.textContent = 'Try again';
      submit.disabled = false;
    }
  });
}

/* ============================================================
   ADDED TO BAG MODAL
   ============================================================ */
const AtbModal = {
  el: null,
  overlay: null,
  close: null,
  productEl: null,

  init() {
    this.el = $('#added-to-bag-modal');
    this.overlay = $('#atb-overlay');
    this.close = $('#atb-close');
    this.productEl = $('#atb-product');
    if (!this.el) return;
    this.close && this.close.addEventListener('click', () => this.closeModal());
    this.overlay && this.overlay.addEventListener('click', () => this.closeModal());

    this.el.addEventListener('click', (e) => {
      const cta = e.target.closest('.atb-modal__cta, a[href="/cart"]');
      if (cta) {
        this.closeModal();
        if (window.innerWidth < 768) {
          e.preventDefault();
          CartDrawer.open();
        }
      }
    });

    // Mouse drag-to-scroll implementation for PC browsers
    const grid = $('#atb-suggestions-grid');
    const style = document.createElement('style');
    style.innerHTML = `
      #atb-suggestions-grid .product-card {
        flex: 0 0 100px !important;
        scroll-snap-align: start !important;
      }
      @media (min-width: 768px) {
        #atb-suggestions-grid {
          display: grid !important;
          grid-template-columns: repeat(3, 1fr) !important;
          overflow-x: visible !important;
          scroll-snap-type: none !important;
          cursor: default !important;
        }
        #atb-suggestions-grid .product-card {
          flex: unset !important;
          scroll-snap-align: unset !important;
          width: 100% !important;
        }
      }
    `;
    document.head.appendChild(style);

    if (grid) {
      let isDown = false;
      let startX;
      let scrollLeft;

      grid.addEventListener('mousedown', (e) => {
        isDown = true;
        grid.classList.add('is-dragging');
        startX = e.pageX - grid.offsetLeft;
        scrollLeft = grid.scrollLeft;
      });

      grid.addEventListener('mouseleave', () => {
        isDown = false;
        grid.classList.remove('is-dragging');
      });

      grid.addEventListener('mouseup', () => {
        isDown = false;
        grid.classList.remove('is-dragging');
      });

      grid.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - grid.offsetLeft;
        const walk = (x - startX) * 1.5; // Drag sensitivity multiplier
        grid.scrollLeft = scrollLeft - walk;
      });
    }
  },

  open(item) {
    if (!this.el) return;
    if (this.productEl && item) {
      // Format variant as "Color | Size: XS" from variant_title e.g. "Blue / XS"
      let variantLine = '';
      if (item.variant_title && item.variant_title !== 'Default Title') {
        const parts = item.variant_title.split(' / ');
        if (parts.length >= 2) {
          variantLine = `${parts[0]} | Size: ${parts[1]}`;
        } else {
          variantLine = `Size: ${parts[0]}`;
        }
      }

      this.productEl.innerHTML = `
        ${item.image ? `<img src="${item.image}" alt="${item.title}" style="width:100px;height:150px;object-fit:cover;flex-shrink:0">` : ''}
        <div class="atb-modal__product-info">
          <p class="atb-modal__product-name">${item.product_title || item.title}</p>
          <p class="atb-modal__product-price">${formatMoney(item.final_price)}</p>
          ${variantLine ? `<p class="atb-modal__product-meta" style="text-transform:none">${variantLine}</p>` : ''}
          <a href="/cart" class="btn btn--full atb-modal__cta" style="width:100%">VIEW BAG</a>
        </div>
      `;

      const ctaBtn = $('.atb-modal__cta', this.productEl);
      if (ctaBtn) {
        ctaBtn.addEventListener('click', (e) => {
          this.closeModal();
          if (window.innerWidth < 768) {
            e.preventDefault();
            CartDrawer.open();
          }
        });
      }
    }

    // Dynamically fetch and display product recommendations in the Added-to-Bag Modal
    const suggestionsGrid = $('#atb-suggestions-grid');
    const suggestionsSection = $('#atb-suggestions');
    if (suggestionsGrid && item && item.product_id) {
      // Display the suggestions section and render skeleton pulse loaders
      if (suggestionsSection) suggestionsSection.style.display = 'block';
      suggestionsGrid.innerHTML = `
        <div class="product-card skeleton-card"><div class="skeleton-img"></div></div>
        <div class="product-card skeleton-card"><div class="skeleton-img"></div></div>
        <div class="product-card skeleton-card"><div class="skeleton-img"></div></div>
        <div class="product-card skeleton-card"><div class="skeleton-img"></div></div>
        <div class="product-card skeleton-card"><div class="skeleton-img"></div></div>
        <div class="product-card skeleton-card"><div class="skeleton-img"></div></div>
      `;

      fetch(`/recommendations/products.json?product_id=${item.product_id}&limit=6`)
        .then(response => response.json())
        .then(data => {
          if (data && data.products && data.products.length > 0) {
            suggestionsGrid.innerHTML = data.products.map(p => {
              const firstImage = p.images && p.images[0] ? p.images[0] : '';
              return `
                <div class="product-card">
                  <a href="${p.url}" class="product-card__img-wrap" style="display:block">
                    ${firstImage ? `<img class="product-card__img" src="${firstImage}" alt="${p.title}" loading="lazy">` : `<div style="width:100%;aspect-ratio:3/4;background:var(--color-muted)"></div>`}
                  </a>
                </div>
              `;
            }).join('');
            if (suggestionsSection) suggestionsSection.style.display = 'block';
          } else {
            // Hide section if there are no suggestions available
            if (suggestionsSection) suggestionsSection.style.display = 'none';
            suggestionsGrid.innerHTML = '';
          }
        })
        .catch(err => console.error('Error fetching ATB suggestions:', err));
    }

    this.el.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('atb-open');
  },

  closeModal() {
    this.el && this.el.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.body.classList.remove('atb-open');
  }
};

function initAtbModal() {
  AtbModal.init();
}

/* ============================================================
   MOBILE NAV
   ============================================================ */
function initMobileNav() {
  const menuBtn = $('#mobile-menu-btn');
  const searchBtn = $('#mobile-search-btn');

  menuBtn && menuBtn.addEventListener('click', () => {
    const navDrawer = $('#nav-drawer');
    if (!navDrawer) return;
    // Toggle: tapping MENU opens the drawer, tapping again closes it (mobile has no X)
    navDrawer.classList.contains('is-open') ? NavDrawer.close() : NavDrawer.open();
  });

  searchBtn && searchBtn.addEventListener('click', (e) => {
    e.preventDefault();
    const navDrawer = $('#nav-drawer');
    if (navDrawer && navDrawer.classList.contains('is-open')) {
      NavDrawer.close();
    }
    // SearchModal.open() now flushes layout and focuses synchronously, which
    // keeps the focus inside this tap gesture — required for the iOS keyboard.
    // Do not wrap this call in a timeout or await anything before it.
    SearchModal.open();
  });
}

/* ============================================================
   PINCODE CHECKER
   ============================================================ */
function initPincodeChecker() {
  // Handled dynamically by shiprocket-pincode.js
  return;
}

/* ---- Wishlist (localStorage) ---------------------------- */
(function () {
  var KEY = 'bluer_wishlist';

  function read() {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; }
    catch (e) { return []; }
  }
  function write(list) {
    localStorage.setItem(KEY, JSON.stringify(list));
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = list.length;
      el.style.display = list.length ? '' : 'none';
    });
  }
  function has(list, handle) {
    return list.some(function (i) { return i.handle === handle; });
  }

  function syncButtons() {
    var list = read();
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      var on = has(list, btn.getAttribute('data-handle'));
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function toggle(btn) {
    var list = read();
    var handle = btn.getAttribute('data-handle');
    if (has(list, handle)) {
      list = list.filter(function (i) { return i.handle !== handle; });
    } else {
      list.push({
        handle: handle,
        title: btn.getAttribute('data-title'),
        url: btn.getAttribute('data-url'),
        price: btn.getAttribute('data-price'),
        image: btn.getAttribute('data-image')
      });
    }
    write(list);
    syncButtons();
    if (window.renderWishlist) window.renderWishlist();
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    toggle(btn);
  });

  // Render the wishlist page grid, if present
  window.renderWishlist = function () {
    var grid = document.getElementById('wishlist-grid');
    var empty = document.getElementById('wishlist-empty');
    if (!grid) return;
    var list = read();
    if (!list.length) {
      grid.innerHTML = '';
      if (empty) empty.style.display = '';
      grid.style.display = 'none';
      return;
    }
    if (empty) empty.style.display = 'none';
    grid.style.display = '';
    grid.innerHTML = list.map(function (i) {
      return '' +
        '<div class="product-card">' +
        '<a href="' + i.url + '" class="product-card__img-wrap" style="display:block">' +
        (i.image ? '<img class="product-card__img" src="' + i.image + '" alt="' + (i.title || '') + '" loading="lazy">' : '<div style="width:100%;aspect-ratio:3/4;background:var(--color-muted)"></div>') +
        '</a>' +
        '<button class="product-card__wishlist is-active btn-reset" data-wishlist-toggle data-handle="' + i.handle + '" data-title="' + (i.title || '') + '" data-url="' + i.url + '" data-price="' + (i.price || '') + '" data-image="' + (i.image || '') + '" aria-pressed="true" aria-label="Remove from wishlist">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 21s-6.716-4.297-9.193-7.07C.96 12.06 1.06 9.36 2.81 7.86c1.6-1.37 3.99-1.13 5.46.36L12 11.94l3.73-3.72c1.47-1.49 3.86-1.73 5.46-.36 1.75 1.5 1.85 4.2.003 6.07C18.716 16.703 12 21 12 21z"/></svg>' +
        '</button>' +
        '<div class="product-card__info">' +
        '<p class="product-card__title"><a href="' + i.url + '">' + (i.title || '') + '</a></p>' +
        '<p class="product-card__price">' + (i.price || '') + '</p>' +
        '</div>' +
        '</div>';
    }).join('');
  };

  syncButtons();
  write(read());
  if (window.renderWishlist) window.renderWishlist();
})();

/* ---- Newsletter AJAX submit ----------------------------- */
(function () {
  var form = document.getElementById('footer-newsletter-form');
  if (!form) return;

  form.addEventListener('submit', function (e) {
    e.preventDefault();

    var email = form.querySelector('input[type="email"]');
    if (!email || !email.value) return;

    var data = new FormData(form);

    fetch(form.action, {
      method: 'POST',
      body: data,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (res) {
        if (res.ok || res.status === 200 || res.redirected) {
          // Show success inline — hide the email row, show success msg
          var row = form.querySelector('.footer-email-row');
          if (row) row.style.display = 'none';
          var success = form.querySelector('.footer-newsletter__success');
          if (!success) {
            success = document.createElement('p');
            success.className = 'footer-newsletter__success';
            success.textContent = 'Thank you for subscribing!';
            form.appendChild(success);
          }
          success.style.display = '';
        } else {
          // Fallback: let native form submit handle it
          form.removeEventListener('submit', arguments.callee);
          form.submit();
        }
      })
      .catch(function () {
        // Network error — fallback to native submit
        form.submit();
      });
  });

  // Global listener: hide mobile bottom nav while typing or input focused
  document.addEventListener('focusin', function (e) {
    if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) {
      document.body.classList.add('is-input-focused');
      document.documentElement.classList.add('is-input-focused');
    }
  });

  document.addEventListener('focusout', function (e) {
    if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) {
      setTimeout(function () {
        var active = document.activeElement;
        if (!active || (active.tagName !== 'INPUT' && active.tagName !== 'TEXTAREA')) {
          document.body.classList.remove('is-input-focused');
          document.documentElement.classList.remove('is-input-focused');
        }
      }, 50);
    }
  });

  // Tap outside input: blur active input to close keyboard and reveal bottom bar
  var handleOutsideTap = function (e) {
    var active = document.activeElement;
    if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) {
      // The search button's own handler focuses the search field, then THIS
      // listener receives the same bubbling tap, sees the tap target is not an
      // <input>, and blurs the field we just focused. On Android the keyboard
      // had already been committed so it survived; on iOS the blur landed in
      // the same gesture and the keyboard never appeared. That was the whole
      // Android/iPhone difference. Never blur when the tap opened the search.
      if (e.target.closest && e.target.closest('#mobile-search-btn, #menu-toggle, .header-search, #search-modal-container')) {
        return;
      }
      if (!e.target.closest('input') && !e.target.closest('textarea') && !e.target.closest('#search-clear-btn')) {
        active.blur();
      }
    }
  };
  document.addEventListener('touchstart', handleOutsideTap, { passive: true });
  document.addEventListener('click', handleOutsideTap);

  // On cart page desktop: redirect wheel scroll from right summary to left items column
  if (document.body.classList.contains('template-cart')) {
    const cartSummary = document.querySelector('.cart-summary');
    const cartItems = document.querySelector('.cart-items');
    if (cartSummary && cartItems) {
      cartSummary.addEventListener('wheel', function (e) {
        if (window.innerWidth >= 992) {
          e.preventDefault();
          cartItems.scrollTop += e.deltaY;
        }
      }, { passive: false });
    }
  }
})();